import { getProductCodename } from '@core/billing-plans-legacy';
import type { BillingPlan } from '@core/products-types';
import { CompanyVerificationRequestStatus } from '@core/spam-verification-data';

import { isDowngrade, isUpgrade } from '../../../helpers/compare-product';
import { getProductCodeOrThrow } from '../../../helpers/get-product-code-or-throw';
import type { PlanPickerStateFactoryData } from '../plan-picker.types';
import { ProductStatus } from '../plan-picker.types';

// eslint-disable-next-line max-lines-per-function
export const getPlanPickerProductStatus = (
  {
    isSelfService,
    isTrial,
    isSubscriptionBlocked,
    activeProduct,
    slotsTotal,
    intervalDirty,
    spamVerificationStatus,
  }: Pick<
    PlanPickerStateFactoryData,
    | 'isSubscriptionBlocked'
    | 'intervalDirty'
    | 'isSelfService'
    | 'isTrial'
    | 'activeProduct'
    | 'slotsTotal'
    | 'spamVerificationStatus'
  >,
  selectedSlots: number,
  product?: BillingPlan,
  assignedProduct?: BillingPlan,
): ProductStatus => {
  if (isSelfService) {
    if (!Number.isInteger(selectedSlots)) {
      return ProductStatus.ManualService;
    }

    if (assignedProduct) {
      if (assignedProduct.id === product?.id) {
        return ProductStatus.SelfService;
      } else {
        return ProductStatus.Unavailable;
      }
    }

    if (activeProduct.custom) {
      if (product?.id !== activeProduct.id) {
        return ProductStatus.Unavailable;
      }
    }

    if (!product) {
      return ProductStatus.ManualService;
    }

    if (isTrial || isSubscriptionBlocked) {
      if (
        [
          CompanyVerificationRequestStatus.Rejected,
          CompanyVerificationRequestStatus.FormSubmitted,
        ].includes(spamVerificationStatus)
      ) {
        return ProductStatus.ManualService;
      }

      return ProductStatus.SelfService;
    }

    if (activeProduct.tier === product.tier) {
      const codename = getProductCodeOrThrow(getProductCodename(activeProduct));
      const beforeChange = {
        codename,
        slots: slotsTotal,
      };

      const afterChange = {
        codename,
        slots: selectedSlots,
      };

      if (isUpgrade(beforeChange, afterChange)) {
        return ProductStatus.SelfServiceUpgrade;
      }

      if (isDowngrade(beforeChange, afterChange)) {
        return ProductStatus.SelfServiceDowngrade;
      }

      if (intervalDirty) {
        return ProductStatus.SelfService;
      }

      return ProductStatus.Owned;
    }

    const a = {
      codename: getProductCodeOrThrow(getProductCodename(activeProduct)),
      slots: slotsTotal,
    };
    const b = {
      codename: getProductCodeOrThrow(getProductCodename(product)),
      slots: slotsTotal,
    };

    if (isUpgrade(a, b)) {
      return ProductStatus.SelfServiceUpgrade;
    }

    if (isDowngrade(a, b)) {
      return ProductStatus.SelfServiceDowngrade;
    }

    return ProductStatus.SelfService;
  }

  if (product && activeProduct.id === product.id) {
    return ProductStatus.ManualServiceOwned;
  }

  return ProductStatus.ManualService;
};
